import {
  buildRoomApiUrl,
  isRoomPersistenceConfigured
} from "../utils/room-persistence-utils";

export function unwrapPersistenceResponse(raw) {
  if (!raw) {
    return null;
  }

  if (Object.prototype.hasOwnProperty.call(raw, "success") && Object.prototype.hasOwnProperty.call(raw, "data")) {
    return raw.data;
  }

  return raw;
}

function buildHeaders(apiKey, hasBody, accept = "application/json", extraHeaders = {}) {
  const headers = {
    Accept: accept,
    ...extraHeaders
  };

  if (hasBody) {
    headers["Content-Type"] = "application/json";
  }

  if (apiKey) {
    headers["x-api-key"] = apiKey;
  }

  return headers;
}

function bearerTokenHeader(token) {
  if (!token || typeof token !== "string") {
    return null;
  }

  return /^Bearer\s+/i.test(token) ? token : `Bearer ${token}`;
}

async function defaultGetAuthHeaders(url, method) {
  const tokenModule = await import("../utils/getToken");
  return tokenModule.getAuthHeaders(url, method);
}

function parseContentDispositionFilename(contentDisposition) {
  if (typeof contentDisposition !== "string") {
    return null;
  }

  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1].replace(/"/g, ""));
  }

  const filenameMatch = contentDisposition.match(/filename="?([^"]+)"?/i);
  return filenameMatch?.[1] || null;
}

function wait(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

export class RoomPersistenceApi {
  constructor({
    dashboardApiUrl,
    nestApiUrl,
    fetch: fetchImpl,
    getAuthHeaders,
    getAuthToken
  } = {}) {
    this.nestApiUrl = nestApiUrl || dashboardApiUrl || "";
    this.fetch = fetchImpl || (typeof window !== "undefined" && window.fetch ? window.fetch.bind(window) : null);
    this.getAuthHeaders = getAuthHeaders || (getAuthToken ? null : defaultGetAuthHeaders);
    this.getAuthToken = getAuthToken || null;
  }

  isConfigured() {
    return isRoomPersistenceConfigured({ persistenceApiUrl: this.nestApiUrl }) && Boolean(this.fetch);
  }

  isSummaryConfigured() {
    return this.isConfigured();
  }

  isChatHistoryDownloadConfigured() {
    return this.isConfigured();
  }

  isTranscriptionHistoryDownloadConfigured() {
    return this.isConfigured();
  }

  async appendRoomChatMessage(payload) {
    return null;
  }

  async appendRoomTranscriptionSegment(payload) {
    return null;
  }

  async generateSummary(payload) {
    return this.nestRequest(`/runtime/rooms/${encodeURIComponent(payload.roomId)}/summaries`, {
      method: "POST",
      body: {
        from: payload.from,
        to: payload.to,
        data_sources: Array.isArray(payload.dataSources) ? payload.dataSources : [],
        notification_recipients: Array.isArray(payload.notificationRecipients) ? payload.notificationRecipients : [],
        ...(payload.summaryAgentUuid ? { summary_agent_uuid: payload.summaryAgentUuid } : {})
      },
      includeAuth: true
    });
  }

  async getArtifactCapabilities(roomId) {
    if (!this.isConfigured() || !roomId) return null;
    return this.nestRequest(`/runtime/rooms/${encodeURIComponent(roomId)}/artifacts/capabilities`, { method: "GET", includeAuth: true });
  }

  async canUploadWorkspaceStorage(workspaceId, { purpose, format, sizeBytes } = {}) {
    if (!this.isConfigured() || !workspaceId) {
      return null;
    }

    const body = {
      ...(purpose ? { purpose } : {}),
      ...(format ? { format } : {}),
      ...(Number.isFinite(sizeBytes) ? { sizeBytes } : {})
    };

    return null;
  }

  async getSummaryStatus(summaryId, { roomId } = {}) {
    if (!this.isConfigured() || !summaryId || !roomId) {
      return null;
    }
    return this.nestRequest(`/runtime/rooms/${encodeURIComponent(roomId)}/summaries/${encodeURIComponent(summaryId)}/status`, {
      method: "GET",
      includeAuth: true,
      includeApiKey: false
    });
  }

  async pollSummary(summaryId, { roomId, intervalMs = 2000, timeoutMs = 120000 } = {}) {
    if (!this.isConfigured() || !summaryId) {
      return null;
    }

    const startedAt = Date.now();
    for (;;) {
      const summary = await this.getSummaryStatus(summaryId, { roomId });
      const status = summary?.status;

      if (status === "failed") {
        throw new Error(summary?.errorMessage || summary?.error || "Summary generation failed");
      }

      if (status !== "queued" && status !== "processing") {
        return summary;
      }

      if (Date.now() - startedAt >= timeoutMs) {
        throw new Error("Summary generation timed out");
      }

      if (intervalMs > 0) {
        await wait(intervalMs);
      }
    }
  }

  async createSummaryDownloadGrant(summaryId, { format = "pdf" } = {}) {
    return null;
  }

  async downloadSummary(summaryId, { format = "pdf" } = {}) {
    if (!this.isConfigured() || !summaryId) {
      return null;
    }

    const encodedSummaryId = encodeURIComponent(summaryId);
    const url = buildRoomApiUrl(this.nestApiUrl, `/summaries/${encodedSummaryId}/download`, { format });
    const authHeaders = await this.authHeaders(url, "GET");
    const response = await this.fetch(url, {
      method: "GET",
      headers: buildHeaders(null, false, format === "pdf" ? "application/pdf" : "*/*", authHeaders)
    });

    if (!response.ok) {
      const raw = await response.json?.().catch(() => null);
      const message =
        raw?.data?.error ||
        raw?.error ||
        raw?.message ||
        response.statusText ||
        `Room persistence download failed (${response.status})`;
      throw new Error(message);
    }

    const blob = await response.blob();
    const filename =
      parseContentDispositionFilename(response.headers?.get?.("content-disposition")) || `${summaryId}.${format}`;
    const contentType = response.headers?.get?.("content-type") || blob?.type || null;
    return { blob, filename, contentType };
  }

  async downloadChatHistory({ roomId, timezone } = {}) {
    if (!this.isChatHistoryDownloadConfigured() || !roomId) {
      return null;
    }

    const url = buildRoomApiUrl(
      this.nestApiUrl,
      `/runtime/rooms/${encodeURIComponent(roomId)}/artifacts/chat/download`,
      {
        timezone
      }
    );
    const authHeaders = await this.authHeaders(url, "GET");
    const response = await this.fetch(url, {
      method: "GET",
      headers: buildHeaders(null, false, "application/zip", authHeaders)
    });

    if (!response.ok) {
      const raw = await response.json?.().catch(() => null);
      const message =
        raw?.data?.error ||
        raw?.error ||
        raw?.message ||
        response.statusText ||
        `Room persistence chat history download failed (${response.status})`;
      throw new Error(message);
    }

    const blob = await response.blob();
    const filename =
      parseContentDispositionFilename(response.headers?.get?.("content-disposition")) || `${roomId}-chat-logs.zip`;
    const contentType = response.headers?.get?.("content-type") || blob?.type || null;
    return { blob, filename, contentType };
  }

  async downloadTranscriptionHistory({ roomId, timezone } = {}) {
    if (!this.isTranscriptionHistoryDownloadConfigured() || !roomId) {
      return null;
    }

    const url = buildRoomApiUrl(
      this.nestApiUrl,
      `/runtime/rooms/${encodeURIComponent(roomId)}/artifacts/transcription/download`,
      {
        timezone
      }
    );
    const authHeaders = await this.authHeaders(url, "GET");
    const response = await this.fetch(url, {
      method: "GET",
      headers: buildHeaders(null, false, "application/zip", authHeaders)
    });

    if (!response.ok) {
      const raw = await response.json?.().catch(() => null);
      const message =
        raw?.data?.error ||
        raw?.error ||
        raw?.message ||
        response.statusText ||
        `Room persistence transcription history download failed (${response.status})`;
      throw new Error(message);
    }

    const blob = await response.blob();
    const filename =
      parseContentDispositionFilename(response.headers?.get?.("content-disposition")) ||
      `${roomId}-transcription-history.zip`;
    const contentType = response.headers?.get?.("content-type") || blob?.type || null;
    return { blob, filename, contentType };
  }

  async nestRequest(path, options = {}) {
    if (!this.isConfigured()) {
      return null;
    }
    return this.request(this.nestApiUrl, path, { ...options, includeApiKey: false });
  }

  async request(
    baseUrl,
    path,
    { method = "GET", body, query, allowMissing = false, includeAuth = false, includeApiKey = false } = {}
  ) {
    const url = buildRoomApiUrl(baseUrl, path, query);
    const hasBody = body !== undefined;
    const authHeaders = includeAuth ? await this.authHeaders(url, method) : {};
    const response = await this.fetch(url, {
      method,
      headers: buildHeaders(null, hasBody, "application/json", authHeaders),
      body: hasBody ? JSON.stringify(body) : undefined
    });

    const raw = await response.json().catch(() => null);
    if (allowMissing && response.status === 404) {
      return null;
    }

    if (!response.ok) {
      const data = raw?.data || raw || null;
      const message =
        raw?.data?.error ||
        raw?.error ||
        raw?.message ||
        response.statusText ||
        `Room persistence request failed (${response.status})`;
      const error = new Error(message);
      error.status = response.status;
      error.data = data;
      error.response = { status: response.status, data };
      throw error;
    }

    return unwrapPersistenceResponse(raw);
  }

  async authHeaders(url, method) {
    try {
      if (this.getAuthHeaders) {
        return (await this.getAuthHeaders(url, method)) || {};
      }

      const token = await this.getAuthToken?.();
      const authorization = bearerTokenHeader(token);
      return authorization ? { Authorization: authorization } : {};
    } catch (error) {
      console.warn("[RoomPersistenceApi] Failed to resolve auth token", error);
      return {};
    }
  }
}
